/**
 * The Gallery's threads: an original and the revisions "Change this…" made
 * of it. The list holds originals with a count; a thread is fetched when one
 * is opened and kept by its original's id. Pure, so the hook stays thin.
 */

import type { GalleryItem } from '@wire';

export type Threads = Record<string, GalleryItem[]>;

/** The original's id, which keys its thread. */
export const rootOf = (item: GalleryItem): string => item.rootId ?? item.id;

/** A thread as it is known: fetched, or the original alone when it has no
 *  revisions to fetch. Null while one with revisions is still coming. */
export function threadOf(original: GalleryItem, threads: Threads): GalleryItem[] | null {
  return threads[original.id] ?? (original.revisions ? null : [original]);
}

/** A revision landed: it ends its thread, and its original counts one more. */
export function withRevision(
  items: GalleryItem[],
  threads: Threads,
  revision: GalleryItem
): { items: GalleryItem[]; threads: Threads } {
  const root = rootOf(revision);
  const original = items.find((i) => i.id === root);
  const known = threads[root] ?? (original && !original.revisions ? [original] : null);
  return {
    items: items.map((i) => (i.id === root ? { ...i, revisions: i.revisions + 1 } : i)),
    // A thread not fetched yet is left to the fetch, which will include it.
    threads: known ? { ...threads, [root]: [...known.filter((i) => i.id !== revision.id), revision] } : threads
  };
}

/** An image went: an original takes its thread with it, a revision goes alone. */
export function withoutImage(
  items: GalleryItem[],
  threads: Threads,
  gone: GalleryItem
): { items: GalleryItem[]; threads: Threads } {
  const root = rootOf(gone);
  if (root === gone.id) {
    const rest = { ...threads };
    delete rest[root];
    return { items: items.filter((i) => i.id !== root), threads: rest };
  }
  return {
    items: items.map((i) => (i.id === root ? { ...i, revisions: Math.max(0, i.revisions - 1) } : i)),
    threads: threads[root] ? { ...threads, [root]: threads[root]!.filter((i) => i.id !== gone.id) } : threads
  };
}
